import React, { useEffect, useState } from 'react';
import { Select, Modal } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { useLoading } from '../contexts/LoadingContext';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import ExchangeIcon from '../assets/icons/exchange.svg';
import '../styles/ConfrontoIMUGraph.css';

const ConfrontoIMUGraph = ({ arco }) => {
  console.log("🚀 ~ ConfrontoIMUGraph ~ arco:", arco)
  const [data, setData] = useState([]);
  const { showLoader, hideLoader } = useLoading();
  const [loadingData, setLoadingData] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFactors, setSelectedFactors] = useState([null, null]);
  const [roadDataOptions, setRoadDataOptions] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine display mode - count UNIQUE roads, not total records
  const uniqueRoadCount = arco && arco.length > 0
    ? new Set(arco.map(item => item.UnifiedID || item.hash).filter(Boolean)).size
    : 0;

  const hasRoads = uniqueRoadCount > 0;
  const hasMultipleRoads = uniqueRoadCount >= 2;
  const isSingleRoad = uniqueRoadCount === 1;

  console.log("Unique roads count:", uniqueRoadCount, "hasRoads:", hasRoads, "hasMultipleRoads:", hasMultipleRoads, "isSingleRoad:", isSingleRoad);

  // Build options for road metrics based on metrics.json fields
  useEffect(() => {
    const roadCategories = [
      {
        value: 'velocita',
        label: 'Velocità Media',
        field: 'AvgSp',
      },
      {
        value: 'velocita_p50',
        label: 'Velocità Mediana (P50)',
        field: 'P50sp',
      },
      {
        value: 'tempo_percorrenza',
        label: 'Tempo di percorrenza',
        field: 'AvgTt',
      },
      {
        value: 'flusso',
        label: 'Flusso',
        field: 'Flusso_Espanso',
      }
    ];

    setRoadDataOptions(roadCategories);

    // Auto-select default metrics if not already selected
    if (!selectedFactors[0] && arco && arco.length > 0) {
      console.log("Auto-selecting default metric: Velocità Media");
      const uniqueRoads = new Set(arco.map(item => item.UnifiedID || item.hash).filter(Boolean)).size;
      const isMultiple = uniqueRoads >= 2;
      setSelectedFactors([roadCategories[0], isMultiple ? roadCategories[0] : roadCategories[1]]);
    }
  }, [arco, selectedFactors]);

  // Fetch available dates from arco data
  useEffect(() => {
    if (!arco || arco.length === 0) {
      setAvailableDates([]);
      setSelectedDate(null);
      return;
    }

    // Extract unique dates from data
    // Priority order:
    // 1. t_start field (format: "2024-08-01 00:00:00")
    // 2. day_type + hour fields (saronno format: day_type as text key)
    // 3. day_group_label field with date patterns
    const dates = new Set();
    arco.forEach(item => {
      // Priority 1: t_start field (format: "2024-08-01 00:00:00")
      if (item.t_start && typeof item.t_start === 'string' && item.t_start.trim() !== '') {
        const parts = item.t_start.split(' ');
        if (parts.length >= 1) {
          const datePart = parts[0];
          if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dates.add(datePart);
            return; // Found date from t_start, skip other checks
          }
        }
      }

      // Priority 2: day_type + hour fields (saronno format)
      if (item.day_type && item.hour !== undefined && item.hour !== null && item.hour !== '') {
        // Use day_type as the date key (e.g., "festivo", "sabato", "feriale")
        dates.add(item.day_type);
        return;
      }

      // Priority 3: day_group_label field with date patterns
      if (item.day_group_label) {
        const dayGroupParts = item.day_group_label.split('_')[0];

        // Check if it's new format (YYYY-MM-DD)
        const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (newFormatMatch) {
          dates.add(dayGroupParts);
        } else {
          // Old format
          const parts = dayGroupParts.split('-');
          if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            dates.add(`${year}-${month}-${day}`);
          } else {
            // Text-based day_group_label - use day_type if available
            if (item.day_type) {
              dates.add(item.day_type);
            }
          }
        }
      }
    });

    const sortedDates = Array.from(dates).sort();
    setAvailableDates(sortedDates);

    // Only auto-select when nothing is selected yet, or the current selection is gone.
    // Do NOT reset on every arco reference change (loader re-renders parent and was
    // snapping the picker back to the first date).
    if (sortedDates.length > 0) {
      setSelectedDate((prev) => {
        if (prev && sortedDates.includes(prev)) return prev;
        return sortedDates[0];
      });
    }
  }, [arco]);

  // Process data when date changes
  useEffect(() => {
    console.log("Processing arco data:", arco);
    console.log("Selected date:", selectedDate);

    if (!arco || arco.length === 0) {
      setData([]);
      setLoadingData(false);
      return;
    }

    setLoadingData(true);
    showLoader("Caricamento dati confronto...");

    // Filter arco data by selected date (if there are multiple dates)
    // If only one date exists, use all data
    let filteredArco = arco;

    if (selectedDate) {
      filteredArco = arco.filter(element => {
        // Priority 1: t_start field (format: "2024-08-01 00:00:00")
        if (element.t_start && typeof element.t_start === 'string' && element.t_start.trim() !== '') {
          const parts = element.t_start.split(' ');
          if (parts.length >= 1) {
            const datePart = parts[0];
            if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return datePart === selectedDate;
            }
          }
        }

        // Priority 2: day_type + hour fields (saronno format)
        if (element.day_type && element.hour !== undefined && element.hour !== null && element.hour !== '') {
          return element.day_type === selectedDate;
        }

        // Priority 3: day_group_label field with date patterns
        if (element.day_group_label) {
          const dayGroupParts = element.day_group_label.split('_')[0];

          // Check if it's new format (YYYY-MM-DD)
          const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (newFormatMatch) {
            return dayGroupParts === selectedDate;
          } else {
            // Old format
            const parts = dayGroupParts.split('-');
            if (parts.length === 3) {
              const day = parts[0].padStart(2, '0');
              const month = parts[1].padStart(2, '0');
              const year = parts[2];
              const recordDate = `${year}-${month}-${day}`;
              return recordDate === selectedDate;
            } else {
              // Text-based day_group_label - match by day_type
              return element.day_type === selectedDate;
            }
          }
        }
        return false;
      });
      console.log("Filtered arco by date:", filteredArco.length, "records");
    } else {
      // No date selected yet - use all data
      console.log("No date selected, using all arco data:", filteredArco.length, "records");
    }

    // Group by UnifiedID/hash to separate different roads
    const roadsByHash = {};
    filteredArco.forEach(element => {
      const hash = element.UnifiedID || element.hash;
      if (!hash) return;
      if (!roadsByHash[hash]) {
        roadsByHash[hash] = [];
      }
      roadsByHash[hash].push(element);
    });

    const roadHashes = Object.keys(roadsByHash);
    console.log("Road hashes found:", roadHashes);

    // Get data for up to 2 roads
    const road1Data = roadHashes[0] ? roadsByHash[roadHashes[0]] : [];
    const road2Data = roadHashes[1] ? roadsByHash[roadHashes[1]] : [];

    console.log("Road 1 data points:", road1Data.length);
    console.log("Road 2 data points:", road2Data.length);

    // Build time series data keyed by t_start
    const timeSeriesMap = {};

    // Define field mappings from arco data to chart keys
    const fieldMappings = {
      velocita: 'AvgSp',
      velocita_p50: 'P50sp',
      tempo_percorrenza: 'AvgTt',
      flusso: 'Flusso_Espanso'
    };

    // Process road 1 data
    road1Data.forEach(element => {
      // Extract time key - use t_start if available, otherwise use hour field
      let timeKey = '00:00';
      let hourKey = 0;

      if (element.t_start && typeof element.t_start === 'string' && element.t_start.trim() !== '') {
        // Format: "2024-08-01 00:00:00" - extract just the time part
        const parts = element.t_start.split(' ');
        if (parts.length >= 2 && parts[1]) {
          timeKey = parts[1].substring(0, 5); // "HH:MM"
        } else {
          timeKey = element.t_start;
        }
        hourKey = element.hour || parseInt(timeKey.split(':')[0], 10) || 0;
      } else if (element.hour !== undefined && element.hour !== null && element.hour !== '') {
        // Use hour field (saronno format - integer 0-23)
        const hourNum = parseInt(element.hour, 10);
        if (!isNaN(hourNum)) {
          timeKey = String(hourNum).padStart(2, '0') + ':00';
          hourKey = hourNum;
        }
      }

      // Create entry if doesn't exist
      if (!timeSeriesMap[timeKey]) {
        timeSeriesMap[timeKey] = {
          timestamp: timeKey,
          hour: hourKey
        };
      }

      // Add all metrics for road 1
      Object.keys(fieldMappings).forEach(metricKey => {
        const field = fieldMappings[metricKey];
        let value = parseFloat(element[field]) || 0;

        // Convert flow to per 15min (divide by 3 or adjust as needed)
        // Flusso_Espanso is used directly as requested
        if (metricKey === 'flusso') {
          value = value;
        }

        timeSeriesMap[timeKey][`${metricKey}_road1`] = value;
      });
    });

    // Process road 2 data
    road2Data.forEach(element => {
      // Extract time key - use t_start if available, otherwise use hour field
      let timeKey = '00:00';
      let hourKey = 0;

      if (element.t_start && typeof element.t_start === 'string' && element.t_start.trim() !== '') {
        // Format: "2024-08-01 00:00:00" - extract just the time part
        const parts = element.t_start.split(' ');
        if (parts.length >= 2 && parts[1]) {
          timeKey = parts[1].substring(0, 5); // "HH:MM"
        } else {
          timeKey = element.t_start;
        }
        hourKey = element.hour || parseInt(timeKey.split(':')[0], 10) || 0;
      } else if (element.hour !== undefined && element.hour !== null && element.hour !== '') {
        // Use hour field (saronno format - integer 0-23)
        const hourNum = parseInt(element.hour, 10);
        if (!isNaN(hourNum)) {
          timeKey = String(hourNum).padStart(2, '0') + ':00';
          hourKey = hourNum;
        }
      }

      // Create entry if doesn't exist
      if (!timeSeriesMap[timeKey]) {
        timeSeriesMap[timeKey] = {
          timestamp: timeKey,
          hour: hourKey
        };
      }

      // Add all metrics for road 2
      Object.keys(fieldMappings).forEach(metricKey => {
        const field = fieldMappings[metricKey];
        let value = parseFloat(element[field]) || 0;

        // Convert flow to per 15min
        // Flusso_Espanso is used directly as requested
        if (metricKey === 'flusso') {
          value = value;
        }

        timeSeriesMap[timeKey][`${metricKey}_road2`] = value;
      });
    });

    // Convert to array and sort by hour and time
    const chartData = Object.values(timeSeriesMap).sort((a, b) => {
      if (a.hour !== b.hour) {
        return a.hour - b.hour;
      }
      return a.timestamp.localeCompare(b.timestamp);
    });

    console.log("Final chart data:", chartData.length, "points");
    console.log("Sample data:", chartData.slice(0, 3));

    // Log the keys in the first data point to verify structure
    if (chartData.length > 0) {
      console.log("First data point keys:", Object.keys(chartData[0]));
      console.log("First data point full:", chartData[0]);

      // Check if values are actually numeric and non-zero
      const firstPoint = chartData[0];
      Object.keys(firstPoint).forEach(key => {
        if (key !== 'timestamp' && key !== 'hour') {
          const value = firstPoint[key];
          console.log(`Key "${key}": value=${value}, type=${typeof value}, isNaN=${isNaN(value)}, isZero=${value === 0}`);
        }
      });
    }

    setData(chartData);
    setLoadingData(false);
    hideLoader();
  }, [selectedDate, arco]);

  // Handle factor selection
  const handleFactorChange = (index, option) => {
    const newFactors = [...selectedFactors];
    newFactors[index] = option;
    setSelectedFactors(newFactors);
  };

  if (loadingData) {
    return null;
  }

  if (!hasRoads) {
    return (
      <div className="proprieta-wrapper">
        <div className="confronto-title">
          <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
          <h3>Confronto</h3>
        </div>
        <div className="confronto-no-data">
          <p>Seleziona almeno una strada per visualizzare il confronto.</p>
        </div>
      </div>
    );
  }

  const hasChartData = data.length > 0 && selectedFactors[0];

  console.log("=== CHART RENDER ===");
  console.log("hasChartData:", hasChartData);
  console.log("data.length:", data.length);
  console.log("selectedFactors[0]:", selectedFactors[0]);
  console.log("selectedFactors[1]:", selectedFactors[1]);
  console.log("hasMultipleRoads:", hasMultipleRoads);
  console.log("isSingleRoad:", isSingleRoad);
  console.log("uniqueRoadCount:", uniqueRoadCount);

  // Check if data actually has the keys we expect
  if (data.length > 0) {
    console.log("ACTUAL data keys in first point:", Object.keys(data[0]));
    console.log("ACTUAL first data point:", data[0]);
  }

  if (selectedFactors[0]) {
    const key1 = `${selectedFactors[0]?.value}_road1`;
    console.log("Expected dataKey for road1:", key1);
    if (data.length > 0) {
      console.log("Key exists in data?:", key1 in data[0], "Value:", data[0][key1]);
    }

    if (hasMultipleRoads && selectedFactors[1]) {
      const key2 = `${selectedFactors[1]?.value}_road2`;
      console.log("Expected dataKey for road2:", key2);
      if (data.length > 0) {
        console.log("Key exists in data?:", key2 in data[0], "Value:", data[0][key2]);
      }
    } else if (selectedFactors[1]) {
      const key2 = `${selectedFactors[1]?.value}_road1`;
      console.log("Expected dataKey for metric2:", key2);
      if (data.length > 0) {
        console.log("Key exists in data?:", key2 in data[0], "Value:", data[0][key2]);
      }
    }
  }

  return (
    <>
      <div className="proprieta-wrapper">
        <div className="confronto-title">
          <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
          <h3>Confronto</h3>
        </div>

        <div className="confronto-content">
          <div className="confronto-date-row">
            <p className="confronto-description">
              {hasMultipleRoads
                ? 'Confronta le metriche tra due strade diverse.'
                : 'Confronta due metriche diverse della stessa strada.'}
            </p>

            <Select
              placeholder="Seleziona data"
              className="confronto-date-picker"
              value={selectedDate}
              onChange={setSelectedDate}
              getPopupContainer={(trigger) => trigger.parentElement}
              popupStyle={{ zIndex: 9999 }}
              style={{ borderRadius: "20px" }}
            >
              {availableDates.map(date => (
                <Select.Option key={date} value={date}>
                  {date && date.match(/^\d{4}-\d{2}-\d{2}$/) ? dayjs(date).format('DD/MM/YYYY') : date}
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* Metric selection */}
          <div className="confronto-dropdowns">
            <div className="dropdown-container">
              <label className="dropdown-label">
                {hasMultipleRoads ? 'Strada 1 - Metrica' : 'Prima Metrica'}
              </label>
              <Select
                className="confronto-select"
                placeholder="Seleziona metrica"
                value={selectedFactors[0]?.value}
                onChange={(value) => {
                  const option = roadDataOptions.find(opt => opt.value === value);
                  handleFactorChange(0, option);
                }}
                getPopupContainer={(trigger) => trigger.parentElement}
                style={{ width: '100%' }}
              >
                {roadDataOptions.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="dropdown-container">
              <label className="dropdown-label">
                {hasMultipleRoads ? 'Strada 2 - Metrica' : 'Seconda Metrica'}
              </label>
              <Select
                className="confronto-select"
                placeholder="Seleziona metrica"
                value={selectedFactors[1]?.value}
                onChange={(value) => {
                  const option = roadDataOptions.find(opt => opt.value === value);
                  handleFactorChange(1, option);
                }}
                getPopupContainer={(trigger) => trigger.parentElement}
                style={{ width: '100%' }}
              >
                {roadDataOptions.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Road info display */}
          {hasRoads && (
            <div className="comparison-info">
              <div className="element-info">
                <h4>Strada 1</h4>
                <p>Hash: {(arco[0]?.UnifiedID || arco[0]?.hash)?.substring(0, 8)}...</p>
                <p>FRC: {arco[0]?.FRC || 'N/A'}</p>
                <p>Data Points: {arco.filter(e => (e.UnifiedID || e.hash) === (arco[0]?.UnifiedID || arco[0]?.hash)).length}</p>
              </div>

              {hasMultipleRoads && (
                <div className="element-info">
                  <h4>Strada 2</h4>
                  <p>Hash: {(arco.find(e => (e.UnifiedID || e.hash) !== (arco[0]?.UnifiedID || arco[0]?.hash))?.UnifiedID || arco.find(e => (e.UnifiedID || e.hash) !== (arco[0]?.UnifiedID || arco[0]?.hash))?.hash)?.substring(0, 8)}...</p>
                  <p>FRC: {arco.find(e => (e.UnifiedID || e.hash) !== (arco[0]?.UnifiedID || arco[0]?.hash))?.FRC || 'N/A'}</p>
                  <p>Data Points: {arco.filter(e => (e.UnifiedID || e.hash) !== (arco[0]?.UnifiedID || arco[0]?.hash)).length}</p>
                </div>
              )}
            </div>
          )}

          {/* Chart */}
          <div className="confronto-chart" style={{ minHeight: '400px', width: '100%', height: '400px' }}>
            <div style={{ position: 'relative', width: '100%', height: '400px' }}>
              {data.length > 0 && selectedFactors[0] && (
                <button
                  onClick={() => setIsExpanded(true)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '36px',
                    height: '36px',
                    backgroundColor: 'rgba(24, 144, 255, 0.9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    zIndex: 100,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Espandi grafico"
                >
                  <ExpandOutlined />
                </button>
              )}

              {data.length > 0 && selectedFactors[0] ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="timestamp"
                    tick={{ fontSize: 11, fill: '#666' }}
                    stroke="#ccc"
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#666' }}
                    axisLine={{ stroke: '#e0e0e0' }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }}
                    formatter={(value) => {
                      if (value == null || Number.isNaN(Number(value))) return value;
                      const n = Number(value);
                      return Number.isInteger(n) ? n : Math.round(n * 100) / 100;
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                  <Line
                    type="monotone"
                    dataKey={`${selectedFactors[0]?.value}_road1`}
                    stroke="#A1E430"
                    strokeWidth={2}
                    dot={false}
                    name={hasMultipleRoads ? `Strada 1: ${selectedFactors[0]?.label}` : selectedFactors[0]?.label}
                    connectNulls
                  />
                  {selectedFactors[1] && hasMultipleRoads && (
                    <Line
                      type="monotone"
                      dataKey={`${selectedFactors[1]?.value}_road2`}
                      stroke="#FF8C42"
                      strokeWidth={2}
                      dot={false}
                      name={`Strada 2: ${selectedFactors[1]?.label}`}
                      connectNulls
                    />
                  )}
                  {selectedFactors[1] && !hasMultipleRoads && (
                    <Line
                      type="monotone"
                      dataKey={`${selectedFactors[1]?.value}_road1`}
                      stroke="#FF8C42"
                      strokeWidth={2}
                      dot={false}
                      name={selectedFactors[1]?.label}
                      connectNulls
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
              ) : (
                <div className="no-selection">
                  <p>Seleziona almeno una metrica per visualizzare il grafico</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Modal */}
      <Modal
        open={isExpanded}
        onCancel={() => setIsExpanded(false)}
        footer={null}
        width="95%"
        centered
        title="Confronto - Vista Espansa"
        destroyOnClose
        zIndex={9999}
        style={{ top: 20 }}
      >
        <div style={{ padding: '20px 0' }}>
          {data.length > 0 && selectedFactors[0] && (
            <ResponsiveContainer width="100%" height={500}>
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="timestamp"
                  tick={{ fontSize: 11, fill: '#666' }}
                  stroke="#ccc"
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#666' }}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }}
                  formatter={(value) => {
                    if (value == null || Number.isNaN(Number(value))) return value;
                    const n = Number(value);
                    return Number.isInteger(n) ? n : Math.round(n * 100) / 100;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                <Line
                  type="monotone"
                  dataKey={`${selectedFactors[0]?.value}_road1`}
                  stroke="#A1E430"
                  strokeWidth={2}
                  dot={false}
                  name={hasMultipleRoads ? `Strada 1: ${selectedFactors[0]?.label}` : selectedFactors[0]?.label}
                  connectNulls
                />
                {selectedFactors[1] && hasMultipleRoads && (
                  <Line
                    type="monotone"
                    dataKey={`${selectedFactors[1]?.value}_road2`}
                    stroke="#FF8C42"
                    strokeWidth={2}
                    dot={false}
                    name={`Strada 2: ${selectedFactors[1]?.label}`}
                    connectNulls
                  />
                )}
                {selectedFactors[1] && !hasMultipleRoads && (
                  <Line
                    type="monotone"
                    dataKey={`${selectedFactors[1]?.value}_road1`}
                    stroke="#FF8C42"
                    strokeWidth={2}
                    dot={false}
                    name={selectedFactors[1]?.label}
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ConfrontoIMUGraph;